import React, { useState, useEffect } from 'react';
import { 
  UserIcon, 
  CameraIcon, 
  PencilIcon, 
  PhotoIcon,
  GlobeAltIcon,
  MapPinIcon,
  PhoneIcon,
  TrophyIcon,
  ChartBarIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { TrophyIcon as TrophyIconSolid } from '@heroicons/react/24/solid';

interface SellerProfile {
  seller_id: number;
  business_name: string;
  contact_name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  business_type: string;
  profile_type: 'basic' | 'premium';
  verification_status: string;
  description: string;
  specialties: string[];
  operating_since: string;
  website_url?: string;
  social_media: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    linkedin?: string;
  };
  business_hours: {
    [key: string]: string;
  };
  images: Array<{
    id: number;
    url: string;
    caption: string;
    is_primary: boolean;
  }>;
  analytics: {
    profile_views: number;
    contact_clicks: number;
    direction_requests: number;
    review_count: number;
    average_rating: number;
  };
}

interface SocialMediaForm {
  facebook: string;
  instagram: string;
  twitter: string;
  linkedin: string;
}

const EnhancedProfile: React.FC = () => {
  const [profile, setProfile] = useState<SellerProfile | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'images' | 'social' | 'analytics' | 'hours'>('profile');
  const [socialMedia, setSocialMedia] = useState<SocialMediaForm>({
    facebook: '',
    instagram: '',
    twitter: '',
    linkedin: ''
  });
  const [businessHours, setBusinessHours] = useState<{ [key: string]: string }>({
    monday: '9:00 AM - 5:00 PM',
    tuesday: '9:00 AM - 5:00 PM',
    wednesday: '9:00 AM - 5:00 PM',
    thursday: '9:00 AM - 5:00 PM',
    friday: '9:00 AM - 5:00 PM',
    saturday: '9:00 AM - 3:00 PM',
    sunday: 'Closed'
  });
  const [newImages, setNewImages] = useState<FileList | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    // Load profile data
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      // Mock data for development
      const mockProfile: SellerProfile = {
        seller_id: 1,
        business_name: "Golden Honey Apiaries",
        contact_name: "John Smith",
        email: "john@goldenhoney.com",
        phone: "(555) 123-4567",
        address: "123 Bee Lane",
        city: "Atlanta",
        state: "GA",
        business_type: "Commercial Apiary",
        profile_type: "premium",
        verification_status: "verified",
        description: "Family-owned apiary specializing in wildflower honey and bee products. Serving the Atlanta area for over 20 years.",
        specialties: ["Wildflower Honey", "Bee Removal", "Hive Equipment", "Queen Bees"],
        operating_since: "2003",
        website_url: "https://goldenhoney.com",
        social_media: {
          facebook: "goldenhoneyapiaries",
          instagram: "goldenhoney_ga",
          twitter: "",
          linkedin: ""
        },
        business_hours: businessHours,
        images: [
          { id: 1, url: "/api/placeholder/400/300", caption: "Our main apiary", is_primary: true },
          { id: 2, url: "/api/placeholder/400/300", caption: "Fresh honey harvest", is_primary: false },
          { id: 3, url: "/api/placeholder/400/300", caption: "Beehive inspection", is_primary: false }
        ],
        analytics: {
          profile_views: 1247,
          contact_clicks: 89,
          direction_requests: 45,
          review_count: 23,
          average_rating: 4.8
        }
      };

      setProfile(mockProfile);
      setSocialMedia({
        facebook: mockProfile.social_media.facebook || '',
        instagram: mockProfile.social_media.instagram || '',
        twitter: mockProfile.social_media.twitter || '',
        linkedin: mockProfile.social_media.linkedin || ''
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  };

  const handleSaveSocialMedia = async () => {
    try {
      // Save social media links
      const response = await fetch('/api/profile/social-media', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(socialMedia)
      });

      if (response.ok) {
        alert('Social media links updated successfully!');
        fetchProfile();
      }
    } catch (error) {
      console.error('Failed to save social media:', error);
      alert('Failed to update social media links');
    }
  };

  const handleSaveBusinessHours = async () => {
    try {
      const response = await fetch('/api/profile/business-hours', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hours: businessHours })
      });

      if (response.ok) {
        alert('Business hours updated successfully!');
        fetchProfile();
      }
    } catch (error) {
      console.error('Failed to save business hours:', error);
      alert('Failed to update business hours');
    }
  };

  const handleImageUpload = async () => {
    if (!newImages || newImages.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(newImages).forEach((file) => {
        formData.append(`images`, file);
      });

      const response = await fetch('/api/profile/images', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        alert('Images uploaded successfully!');
        fetchProfile();
        setNewImages(null);
      }
    } catch (error) {
      console.error('Failed to upload images:', error);
      alert('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const setPrimaryImage = async (imageId: number) => {
    try {
      const response = await fetch(`/api/profile/images/${imageId}/primary`, {
        method: 'PUT'
      });

      if (response.ok) {
        fetchProfile();
      }
    } catch (error) {
      console.error('Failed to set primary image:', error);
    }
  };

  const deleteImage = async (imageId: number) => {
    if (!confirm('Are you sure you want to delete this image?')) return;

    try {
      const response = await fetch(`/api/profile/images/${imageId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        fetchProfile();
      }
    } catch (error) {
      console.error('Failed to delete image:', error);
    }
  };

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Profile Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mr-6">
                <UserIcon className="w-10 h-10 text-amber-600" />
              </div>
              <div>
                <div className="flex items-center mb-2">
                  <h1 className="text-2xl font-bold text-gray-900 mr-3">{profile.business_name}</h1>
                  {profile.profile_type === 'premium' && (
                    <TrophyIconSolid className="w-6 h-6 text-amber-500" />
                  )}
                </div>
                <p className="text-gray-600">{profile.contact_name}</p>
                <p className="text-sm text-gray-500">{profile.city}, {profile.state}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {profile.profile_type === 'premium' && (
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                  PREMIUM
                </span>
              )}
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors flex items-center"
              >
                <PencilIcon className="w-4 h-4 mr-2" />
                {isEditing ? 'Save Changes' : 'Edit Profile'}
              </button>
            </div>
          </div>

          {/* Quick Stats (Premium Feature) */}
          {profile.profile_type === 'premium' && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-4 bg-amber-50 rounded-lg">
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-600">{profile.analytics.profile_views}</div>
                <div className="text-sm text-gray-600">Profile Views</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-600">{profile.analytics.contact_clicks}</div>
                <div className="text-sm text-gray-600">Contact Clicks</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-600">{profile.analytics.direction_requests}</div>
                <div className="text-sm text-gray-600">Directions</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-600">{profile.analytics.review_count}</div>
                <div className="text-sm text-gray-600">Reviews</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-600">{profile.analytics.average_rating.toFixed(1)}</div>
                <div className="text-sm text-gray-600">Avg Rating</div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-lg mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { key: 'profile', label: 'Profile Info', icon: UserIcon },
                { key: 'images', label: 'Photo Gallery', icon: PhotoIcon },
                { key: 'social', label: 'Social Media', icon: GlobeAltIcon },
                { key: 'hours', label: 'Business Hours', icon: MapPinIcon },
                { key: 'analytics', label: 'Analytics', icon: ChartBarIcon, premium: true }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as any)}
                  disabled={tab.premium && profile.profile_type !== 'premium'}
                  className={`py-4 px-2 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.key
                      ? 'border-amber-500 text-amber-600'
                      : tab.premium && profile.profile_type !== 'premium'
                      ? 'border-transparent text-gray-300 cursor-not-allowed'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4 mr-2" />
                  {tab.label}
                  {tab.premium && profile.profile_type !== 'premium' && (
                    <TrophyIcon className="w-4 h-4 ml-1 text-amber-400" />
                  )}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Business Information</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
                      <input
                        type="text"
                        value={profile.business_name}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={profile.description}
                        disabled={!isEditing}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                      <input
                        type="url"
                        value={profile.website_url || ''}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-50"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={profile.phone}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={profile.email}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                      <input
                        type="text"
                        value={profile.address}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-50"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Images Tab */}
            {activeTab === 'images' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold">Photo Gallery</h3>
                  <div className="flex items-center space-x-4">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => setNewImages(e.target.files)}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors cursor-pointer flex items-center"
                    >
                      <CameraIcon className="w-4 h-4 mr-2" />
                      Add Photos
                    </label>
                    {newImages && newImages.length > 0 && (
                      <button
                        onClick={handleImageUpload}
                        disabled={uploading}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        {uploading ? 'Uploading...' : `Upload ${newImages.length} Photo${newImages.length > 1 ? 's' : ''}`}
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {profile.images.map((image) => (
                    <div key={image.id} className="relative group">
                      <img
                        src={image.url}
                        alt={image.caption}
                        className="w-full h-48 object-cover rounded-lg shadow-md"
                      />
                      {image.is_primary && (
                        <div className="absolute top-2 left-2 bg-amber-500 text-white px-2 py-1 rounded text-xs font-medium">
                          Primary
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center space-x-2">
                        {!image.is_primary && (
                          <button
                            onClick={() => setPrimaryImage(image.id)}
                            className="bg-amber-600 text-white px-3 py-1 rounded text-sm hover:bg-amber-700"
                          >
                            Set Primary
                          </button>
                        )}
                        <button
                          onClick={() => deleteImage(image.id)}
                          className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                        >
                          Delete
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">{image.caption}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Social Media Tab */}
            {activeTab === 'social' && (
              <div>
                <h3 className="text-lg font-semibold mb-6">Social Media Integration</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Facebook Page</label>
                    <input
                      type="text"
                      value={socialMedia.facebook}
                      onChange={(e) => setSocialMedia({ ...socialMedia, facebook: e.target.value })}
                      placeholder="facebook.com/your-page"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Instagram</label>
                    <input
                      type="text"
                      value={socialMedia.instagram}
                      onChange={(e) => setSocialMedia({ ...socialMedia, instagram: e.target.value })}
                      placeholder="@your_username"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Twitter</label>
                    <input
                      type="text"
                      value={socialMedia.twitter}
                      onChange={(e) => setSocialMedia({ ...socialMedia, twitter: e.target.value })}
                      placeholder="@your_username"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">LinkedIn</label>
                    <input
                      type="text"
                      value={socialMedia.linkedin}
                      onChange={(e) => setSocialMedia({ ...socialMedia, linkedin: e.target.value })}
                      placeholder="linkedin.com/company/your-company"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>
                </div>
                <div className="mt-6">
                  <button
                    onClick={handleSaveSocialMedia}
                    className="bg-amber-600 text-white px-6 py-2 rounded-lg hover:bg-amber-700 transition-colors"
                  >
                    Save Social Media Links
                  </button>
                </div>
              </div>
            )}

            {/* Business Hours Tab */}
            {activeTab === 'hours' && (
              <div>
                <h3 className="text-lg font-semibold mb-6">Business Hours</h3>
                <div className="space-y-4">
                  {Object.entries(businessHours).map(([day, hours]) => (
                    <div key={day} className="flex items-center space-x-4">
                      <div className="w-24 capitalize font-medium text-gray-700">
                        {day}:
                      </div>
                      <input
                        type="text"
                        value={hours}
                        onChange={(e) => setBusinessHours({ ...businessHours, [day]: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
                        placeholder="e.g., 9:00 AM - 5:00 PM or Closed"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-6">
                  <button
                    onClick={handleSaveBusinessHours}
                    className="bg-amber-600 text-white px-6 py-2 rounded-lg hover:bg-amber-700 transition-colors"
                  >
                    Save Business Hours
                  </button>
                </div>
              </div>
            )}

            {/* Analytics Tab (Premium Only) */}
            {activeTab === 'analytics' && profile.profile_type === 'premium' && (
              <div>
                <h3 className="text-lg font-semibold mb-6">Analytics Dashboard</h3>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <UserIcon className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-2xl font-bold text-blue-600">{profile.analytics.profile_views}</div>
                        <div className="text-sm text-gray-600">Profile Views</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <PhoneIcon className="w-6 h-6 text-green-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-2xl font-bold text-green-600">{profile.analytics.contact_clicks}</div>
                        <div className="text-sm text-gray-600">Contact Clicks</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <MapPinIcon className="w-6 h-6 text-purple-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-2xl font-bold text-purple-600">{profile.analytics.direction_requests}</div>
                        <div className="text-sm text-gray-600">Direction Requests</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-amber-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <StarIcon className="w-6 h-6 text-amber-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-2xl font-bold text-amber-600">{profile.analytics.average_rating.toFixed(1)}</div>
                        <div className="text-sm text-gray-600">Avg Rating</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-semibold mb-4">Performance Insights</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Profile Completion</span>
                      <div className="flex items-center">
                        <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                          <div className="bg-green-600 h-2 rounded-full" style={{ width: '85%' }}></div>
                        </div>
                        <span className="text-sm font-medium">85%</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Customer Engagement</span>
                      <div className="flex items-center">
                        <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                          <div className="bg-blue-600 h-2 rounded-full" style={{ width: '72%' }}></div>
                        </div>
                        <span className="text-sm font-medium">72%</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Search Visibility</span>
                      <div className="flex items-center">
                        <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                          <div className="bg-amber-600 h-2 rounded-full" style={{ width: '91%' }}></div>
                        </div>
                        <span className="text-sm font-medium">91%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedProfile;